import z from 'zod';

import {
    AuthoritativeConceptMappingSchema,
    AuthoritativeConceptSchemeSchema,
    RESEARCH_ONTOLOGY_VERSION,
    ResearchDomainDefinitionSchema,
} from '@/shared/document-intelligence/researchOntology';

import { apiClient } from '../http';

const documentOntologyMappingSchema = AuthoritativeConceptMappingSchema.extend({
    conceptId: z.string().regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/),
});

const documentOntologyCatalogSchema = z.object({
    checksum: z.string().regex(/^[a-f0-9]{64}$/),
    domains: z.array(ResearchDomainDefinitionSchema),
    mappings: z.array(documentOntologyMappingSchema),
    reviewedAt: z.iso.date(),
    schemes: z.array(AuthoritativeConceptSchemeSchema),
    version: z.string().min(1),
});

export type DocumentOntologyCatalog = z.infer<typeof documentOntologyCatalogSchema>;

const CATALOG_CACHE_TTL_MS = 5 * 60 * 1_000;
let cachedCatalog: { expiresAt: number; value: DocumentOntologyCatalog } | undefined;
let catalogRequest: Promise<DocumentOntologyCatalog> | undefined;

export class DocumentOntologyVersionError extends Error {
    constructor(readonly receivedVersion: string) {
        super(`Document ontology ${receivedVersion} is incompatible with matcher ${RESEARCH_ONTOLOGY_VERSION}.`);
        this.name = 'DocumentOntologyVersionError';
    }
}

const validateCatalog = (value: unknown) => {
    const catalog = documentOntologyCatalogSchema.parse(value);

    if (catalog.version !== RESEARCH_ONTOLOGY_VERSION) {
        throw new DocumentOntologyVersionError(catalog.version);
    }

    return catalog;
};

export const getDocumentOntologyCatalog = ({ force = false }: { force?: boolean } = {}) => {
    if (!force && cachedCatalog && cachedCatalog.expiresAt > Date.now()) {
        return Promise.resolve(cachedCatalog.value);
    }
    if (!force && catalogRequest) {
        return catalogRequest;
    }

    const request = apiClient
        .get<unknown>('/api/document-intelligence/ontology')
        .then(validateCatalog)
        .then((catalog) => {
            cachedCatalog = { expiresAt: Date.now() + CATALOG_CACHE_TTL_MS, value: catalog };
            return catalog;
        })
        .finally(() => {
            if (catalogRequest === request) {
                catalogRequest = undefined;
            }
        });

    if (!force) {
        catalogRequest = request;
    }

    return request;
};

export const getOntologyMappingsByConcept = (catalog: DocumentOntologyCatalog) => {
    const mappings = new Map<string, DocumentOntologyCatalog['mappings']>();

    catalog.mappings.forEach((mapping) => {
        mappings.set(mapping.conceptId, [...(mappings.get(mapping.conceptId) ?? []), mapping]);
    });

    return mappings;
};
